import { useState } from "react";
import { Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { useI18n, useL } from "../i18n";
import { withReferral } from "../referral";

// Small inline SVGs for social platforms (avoids emoji + heavy icon libs)
const WA = (p) => (<svg viewBox="0 0 24 24" {...p}><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path fill="currentColor" d="M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 0 0 5.71 1.447h.005c6.581 0 11.94-5.336 11.943-11.896 0-3.176-1.24-6.165-3.473-8.45zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.032-1.378l-.361-.214-3.741.977 1.005-3.638-.235-.374a9.86 9.86 0 0 1-1.516-5.29c.002-5.45 4.455-9.885 9.89-9.885 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.892 6.994c-.002 5.45-4.455 9.91-9.886 9.91z"/></svg>);
const XLogo = (p) => (<svg viewBox="0 0 24 24" {...p}><path fill="currentColor" d="M18.244 2H21.5l-7.19 8.216L23 22h-6.833l-5.27-6.64L4.83 22H1.573l7.695-8.79L1 2h6.98l4.77 6.078L18.244 2Zm-1.197 18h1.88L7.006 4h-2.02l12.061 16Z"/></svg>);
const FB = (p) => (<svg viewBox="0 0 24 24" {...p}><path fill="currentColor" d="M24 12.073C24 5.446 18.627.073 12 .073S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953h-1.514c-1.49 0-1.955.925-1.955 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073Z"/></svg>);
const TG = (p) => (<svg viewBox="0 0 24 24" {...p}><path fill="currentColor" d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>);

function makeUrls({ url, text }) {
  const u = encodeURIComponent(url);
  const t = encodeURIComponent(text);
  return {
    whatsapp: `https://wa.me/?text=${t}%20${u}`,
    x: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
    telegram: `https://t.me/share/url?url=${u}&text=${t}`,
  };
}

export default function SocialShare({ url, text, testidPrefix = "share" }) {
  const { t, lang } = useI18n();
  const L = useL();
  const [copied, setCopied] = useState(false);
  const rawUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const finalUrl = withReferral(rawUrl);
  const links = makeUrls({ url: finalUrl, text: text || "" });

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(finalUrl);
      setCopied(true);
      toast.success(L({ ar: "تم نسخ الرابط", en: "Link copied", fr: "Lien copié" }));
      setTimeout(() => setCopied(false), 1600);
    } catch (err) {
      console.warn("clipboard write failed", err);
      toast.error(L({ ar: "تعذّر النسخ", en: "Copy failed", fr: "Échec de la copie" }));
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: text, text, url: finalUrl }); }
      catch (err) { console.warn("native share cancelled/failed", err); }
    } else {
      copy();
    }
  };

  const Btn = ({ href, onClick, children, label, className = "", testid }) => (
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid={`${testidPrefix}-${testid}`}
        className={`h-11 w-11 rounded-full grid place-items-center border border-[#E6DED3] hover:border-[#1C1917] transition-colors bg-white text-[#1C1917] ${className}`}
      >
        {children}
      </a>
    ) : (
      <button
        onClick={onClick}
        aria-label={label}
        data-testid={`${testidPrefix}-${testid}`}
        className={`h-11 w-11 rounded-full grid place-items-center border border-[#E6DED3] hover:border-[#1C1917] transition-colors bg-white text-[#1C1917] ${className}`}
      >
        {children}
      </button>
    )
  );

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testidPrefix}>
      <span className="text-xs uppercase tracking-widest text-[#78716C] me-2 flex items-center gap-1.5">
        <Share2 className="h-3.5 w-3.5" />
        {L({ ar: "شارك", en: "Share", fr: "Partager" })}
      </span>
      <Btn href={links.whatsapp} label="WhatsApp" testid="whatsapp" className="hover:text-[#25D366]"><WA className="h-4 w-4" /></Btn>
      <Btn href={links.x} label="X (Twitter)" testid="x" className="hover:bg-[#1C1917] hover:text-white"><XLogo className="h-4 w-4" /></Btn>
      <Btn href={links.facebook} label="Facebook" testid="facebook" className="hover:text-[#1877F2]"><FB className="h-4 w-4" /></Btn>
      <Btn href={links.telegram} label="Telegram" testid="telegram" className="hover:text-[#26A5E4]"><TG className="h-4 w-4" /></Btn>
      <Btn onClick={copy} label="Copy link" testid="copy">{copied ? <Check className="h-4 w-4 text-[#2D3A2B]" /> : <Copy className="h-4 w-4" />}</Btn>
      <Btn onClick={nativeShare} label="More" testid="native"><Share2 className="h-4 w-4" /></Btn>
    </div>
  );
}
